import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import DataGrid, {RenderableExpandRow} from "../../../core/components/data-grid/DataGrid";
import {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import ApiService from "../../../core/service/api-service";
import HL7RequestRowMenu from "./HL7RequestRowMenu";
import HL7GridTools from "./HL7GridTools";
import {useModal} from "../../../core/context/ModalContext";
import Colors from "../../../core/style/Colors";
import {Input, notification, Popover, Typography} from "antd";
import HL7ReportRowMenu from "./HL7ReportRowMenu";
import {dateFormat} from "../../../core/utils/date-utils";

import './row-styles.css'

export default function HL7MessageGrid({})
{
    const {setData, setPaginationResult, queryModel, setDataGridProps, setRowColor, refreshDataGrid, setSelectedRows, tableId, loadCount} = useDataGrid();
    const {openModal, closeModal} = useModal()

    // Seçili satır CSS class'ı ile yönetilir (DOM boyama yok). Seçim değişince re-render.
    const selectedRef = useRef(null)
    const [, forceRender] = useState(0)

    const manualMatch = async (messageId, studyId) => {
        if(!studyId)
        {
            notification.error({
                message: t('Study Id is empty')
            })
            return
        }

        const res = await ApiService.HL7Service.ManualMatchHL7(messageId, studyId)
        if(res.success) {
            refreshDataGrid()
        }
    }

    useEffect(() => {
        let columns = [
            {
                title: t('Created Date'),
                dataIndex: 'createdDate',
                key: 'createdDate',
                columnName: 'createdDate',
                sorter: true,
                render: (text, record) => dateFormat(record.createdDate)
            },
            {
                title: t('Hospital'),
                dataIndex: 'hospital',
                key: 'hospital',
                columnName: 'hl7Client.hospital.name',
                sorter: true,
                render: (text, record) => record?.hospital?.name
            },
            {
                title: t('Study'),
                dataIndex: 'studyId',
                key: 'studyId',
                columnName: 'studyId',
                render: (text, record) => (
                    <>
                        <Input.Search
                            size={'small'}
                            placeholder={record?.studyId || '-'}
                            onSearch={(value) => manualMatch(record.id,  value)}
                            style={{ width: 100 }} />
                    </>
                )
            },
            {
                title: t('Patient Id'),
                dataIndex: 'patientId',
                key: 'patientId',
                columnName: 'patientId',
                sorter: true,
                render: (text, record) => `${record?.hl7Message?.patientHbysId} | ${record?.hl7Message?.patientId}`
            },
            {
                title: t('Accession Number'),
                dataIndex: 'accessionNumber',
                key: 'accessionNumber',
                columnName: 'accessionNumber',
                sorter: true,
                render: (text, record) => record?.hl7Message?.accessionNumber
            },
            {
                title: t('Modality'),
                dataIndex: 'modality',
                key: 'modality',
                columnName: 'modality',
                sorter: true,
                render: (text, record) => record?.hl7Message?.modality
            },
            {
                title: t('Request Date'),
                dataIndex: 'createdDate',
                key: 'createdDate',
                columnName: 'createdDate',
                render: (text ,record) => {
                    return (
                        <HL7RequestRowMenu row={record} text={dateFormat(text)} />
                    )
                }
            },
            {
                title: t('Req. Description'),
                dataIndex: 'description',
                key: 'description',
                columnName: 'description',
            },
            {
                title: t('Req. Completed'),
                dataIndex: 'completedDate',
                key: 'completedDate',
                columnName: 'completedDate',
                render: (text ,record) => text ? dateFormat(text) : '-'
            },
            {
                title: t('Req. Status'),
                dataIndex: 'processStatus',
                key: 'processStatus',
                columnName: 'processStatus',
                sorter: true,
                render: (text, record) => (
                    <RequestBody>
                        <StatusText status={text} />
                    </RequestBody>
                )
            },
            {
                title: t('Report Date'),
                dataIndex: '#1',
                key: '#1',
                columnName: '#1',
                render: (text ,record) => {
                    if(record.report) {
                        return (
                            <HL7ReportRowMenu
                                row={record?.report}
                                record={record}
                                text={record?.report?.createdDate ? dateFormat(record?.report?.createdDate) : '-'}
                            />
                        )
                    }
                    else {
                        return '-'
                    }
                }
            },
            {
                title: t('ACK'),
                dataIndex: '#4',
                key: '#4',
                columnName: '#4',
                width: 200,
                render: (text, record) => {
                    let ack = record?.report?.ack
                    let message = '-'
                    if(ack) {
                        const split = ack.split('|').filter(x => x?.toString()?.trim())
                        message = split[split.length-1]
                    } else {
                        ack = '-'
                    }

                    return (
                        <Popover
                            placement={"leftBottom"}
                            content={(
                                <Typography.Text style={{width: 200}}>
                                    <div style={{whiteSpace: 'pre-wrap'}}>
                                        {ack}
                                    </div>
                                </Typography.Text>
                            )}
                        >
                            <Typography.Text ellipsis={true} style={{width: 200, fontSize: '9pt'}}>
                                {message}
                            </Typography.Text>
                        </Popover>
                    )
                }
            },
            {
                title: t('Report Sent Date'),
                dataIndex: '#2',
                key: '#2',
                columnName: '#2',
                render: (text ,record) => record?.report?.completedDate ? dateFormat(record?.report?.completedDate) : '-'
            },
            {
                title: t('Report Status'),
                dataIndex: '#3',
                key: '#3',
                columnName: '#3',
                render: (text, record) => (
                    <ReportBody>
                        {record?.report ? <StatusText status={record.report.processStatus} /> : '-'}
                    </ReportBody>
                )
            },
        ];

        setDataGridProps({
            rowKey: 'id',
            virtual: true,
            columns: columns,
            antdTableProps: {
                scroll: {x: 800},
            },
            rowClassName: (record, index) => {
                return selectedRef.current === record.id ? 'selected-row' : '';
            },
            enableRowSelection: true,
            onRow: (record) => {
                return {
                    onClick: () => {
                        selectedRef.current = record.id;
                        forceRender(x => x + 1);
                    },
                    onDoubleClick: () => {
                        selectedRef.current = record.id;
                        forceRender(x => x + 1);
                    }
                }
            }
        })
    }, [])

    const getHL7Messages = async () => {
        const qm = {...queryModel};
        const response = await ApiService.HL7Service.GetDataGrid(qm);
        if(response.success)
        {
            setData(response.data);
            setPaginationResult(response.pagination)

            // Sayaç ayrı istek, sayfa çizildikten SONRA (bkz. StudyGrid).
            loadCount((exact) => ApiService.HL7Service.GetDataGridCount(qm, exact), JSON.stringify(qm.filter))
        }
    }

    useEffect(() => {
        getHL7Messages()
    }, [queryModel.updateKey])

    return (
        <>
            <DataGrid
                name={'hl7-message-table'}
                title={() => <HL7GridTools />}
            />
        </>
    )
}

// Mirrors HL7ProcessStatusEnum on the backend.
const STATUS_LABELS = {
    0: 'Waiting',
    1: 'Complete',
    2: 'Error',
    3: 'Continue',
    4: 'Removed',
    5: 'Send Error',
}

const ERROR_STATUSES = [2, 5]

const StatusText = ({status}) => {
    const label = STATUS_LABELS[status]
    if (!label)
        return '-'

    return (
        <span style={ERROR_STATUSES.includes(status) ? {color: '#c0392b', fontWeight: 500} : undefined}>
            {t(label)}
        </span>
    )
}

const RequestBody = ({children}) => {
    return (
        <div style={{backgroundColor: '#f6e58d', paddingLeft: 4}}>
            {children}
        </div>
    )
}

const ReportBody = ({children}) => {
    return (
        <div style={{backgroundColor: '#7efff5', paddingLeft: 4}}>
            {children}
        </div>
    )
}